import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AuthChecker from './components/AuthChecker/AuthChecker';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import { ToastProvider } from './contexts/ToastContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { KioskModeProvider } from './contexts/KioskModeContext';
import SignIn from './components/SignIn/SignIn';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import ResetPassword from './components/ResetPassword/ResetPassword';
import PartnerSetup from './components/PartnerSetup/PartnerSetup';
import PartnerLegalAccept from './components/PartnerSetup/PartnerLegalAccept';
import SupplierSetup from './components/PartnerSetup/SupplierSetup';
import { supplierRoutes } from './modules/supplier/routes';
import MarketingRedirect from './components/MarketingRedirect/MarketingRedirect';
import CustomerHomeGate from './components/CustomerHomeGate/CustomerHomeGate';
import RestaurantsList from './components/RestaurantsList/RestaurantsList';
import RestaurantHome from './components/RestaurantHome/RestaurantHome';
import RestaurantReviews from './components/RestaurantReviews/RestaurantReviews';
import RestaurantMenu from './components/RestaurantMenu/RestaurantMenu';
import MenuBrowse from './components/MenuBrowse/MenuBrowse';
import MenuItemDetail from './components/MenuItemDetail/MenuItemDetail';
import Cart from './components/Cart/Cart';
import TableReservation from './components/TableReservation/TableReservation';
import OrderDetails from './components/OrderDetails/OrderDetails';
import MyOrders from './components/MyOrders/MyOrders';
import MyAccount from './components/MyAccount/MyAccount';
import SavedAddresses from './components/SavedAddresses/SavedAddresses';
import JoinOrder from './components/JoinOrder/JoinOrder';
import QueueJoin from './components/QueueJoin/QueueJoin';
import QueueStatus from './components/QueueStatus/QueueStatus';
import GamesHub from './components/Games/GamesHub';
import GamePlay from './components/Games/GamePlay';
import RewardsWallet from './components/RewardsWallet';
import OngoingOrderBar from './components/OngoingOrderBar/OngoingOrderBar';
import OrderFeedbackPrompt from './components/OrderFeedback/OrderFeedbackPrompt';
import MaznutCaptainBridge from './components/VirtualCaptain/MaznutCaptainBridge';
import SentryBootstrap from './components/SentryBootstrap/SentryBootstrap';
import AnalyticsTracker from './components/Analytics/AnalyticsTracker';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import MediaConfigProvider from './components/MediaConfigProvider/MediaConfigProvider';
import KioskLayout from './components/Kiosk/KioskLayout';
import KioskLogin from './components/Kiosk/KioskLogin';
import KioskOrderType from './components/Kiosk/KioskOrderType';
import KioskMenu from './components/Kiosk/KioskMenu';
import KioskConfirmation from './components/Kiosk/KioskConfirmation';
import KioskGuestRouteRedirect from './components/Kiosk/KioskGuestRouteRedirect';
import KioskPaymentReturnRedirect from './components/Kiosk/KioskPaymentReturnRedirect';
import KioskEmbeddedChrome from './components/Kiosk/KioskEmbeddedChrome';
import './components/Kiosk/kioskFabStack.css';
import './App.css';

export function AppShell() {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <SentryBootstrap />
      <AnalyticsTracker />
      <MediaConfigProvider>
      <AuthChecker>
        <ToastProvider>
          <KioskModeProvider>
          <RealtimeProvider>
        <div className="App">
          <div id="peppar-fixed-chrome" />
          <KioskGuestRouteRedirect />
          <KioskPaymentReturnRedirect />
          <KioskEmbeddedChrome />
          <OngoingOrderBar />
          <OrderFeedbackPrompt />
          <MaznutCaptainBridge />
          <Routes>
            <Route path="/signin" element={<SignIn />} />
            <Route path="/forgotPassword" element={<ForgotPassword />} />
            <Route path="/resetPassword" element={<ResetPassword />} />
            <Route path="/partner/setup" element={<PartnerSetup />} />
            <Route path="/partner/legal-accept" element={<PartnerLegalAccept />} />
            <Route path="/partner/setup/supplier" element={<SupplierSetup />} />
            {supplierRoutes}
            <Route path="/about" element={<MarketingRedirect to="/about" />} />
            <Route path="/contact" element={<MarketingRedirect to="/contact" />} />
            <Route path="/help" element={<MarketingRedirect to="/help" />} />
            <Route path="/faqs" element={<MarketingRedirect to="/faqs" />} />
            <Route path="/blog" element={<MarketingRedirect to="/blog" />} />
            <Route path="/blog/:slug" element={<MarketingRedirect />} />
            <Route path="/terms-and-conditions" element={<MarketingRedirect to="/terms-and-conditions" />} />
            <Route path="/privacy-and-policy" element={<MarketingRedirect to="/privacy-policy" />} />
            <Route path="/" element={<CustomerHomeGate />} />
            <Route path="/restaurants" element={<RestaurantsList />} />
            <Route path="/restaurants/:appName" element={<RestaurantHome />} />
            <Route path="/restaurants/:appName/reviews" element={<RestaurantReviews />} />
            <Route path="/restaurants/:appName/menu" element={<RestaurantMenu />} />
            <Route path="/restaurants/:appName/browse" element={<MenuBrowse />} />
            <Route path="/restaurants/:appName/menu/:itemUrl" element={<MenuItemDetail />} />
            <Route path="/restaurants/:appName/cart" element={<Cart />} />
            <Route path="/restaurants/:appName/reserve-table" element={<TableReservation />} />
            <Route path="/restaurants/:appName/orders/:orderId" element={<OrderDetails />} />
            <Route path="/restaurants/:appName/my-orders" element={<MyOrders />} />
            <Route path="/restaurants/:appName/rewards" element={<RewardsWallet />} />
            <Route path="/my-orders" element={<MyOrders />} />
            <Route path="/restaurants/:appName/my-account" element={<MyAccount />} />
            <Route path="/my-account" element={<MyAccount />} />
            <Route path="/restaurants/:appName/saved-addresses" element={<SavedAddresses />} />
            <Route path="/saved-addresses" element={<SavedAddresses />} />
            <Route path="/restaurants/:appName/join-order" element={<JoinOrder />} />
            <Route path="/restaurants/:appName/join-queue" element={<QueueJoin />} />
            <Route path="/restaurants/:appName/queue/:publicRef" element={<QueueStatus />} />
            <Route path="/restaurants/:appName/games" element={<GamesHub />} />
            <Route path="/restaurants/:appName/games/:gameType" element={<GamePlay />} />
            <Route path="/kiosk/:appName" element={<KioskLayout />}>
              <Route path="login" element={<KioskLogin />} />
              <Route path="order-type" element={<KioskOrderType />} />
              <Route path="menu" element={<KioskMenu />} />
              <Route path="menu/:itemUrl" element={<KioskMenu />} />
              <Route path="cart" element={<Cart />} />
              <Route path="confirmation" element={<KioskConfirmation />} />
              <Route path="orders" element={<MyOrders />} />
              <Route path="orders/:orderId" element={<OrderDetails />} />
            </Route>
          </Routes>
        </div>
          </RealtimeProvider>
          </KioskModeProvider>
        </ToastProvider>
      </AuthChecker>
      </MediaConfigProvider>
      </ErrorBoundary>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
